/* eslint-disable */
import { ElementType, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { toggleChat, addUserMessage, renderCustomComponent } from '../../store/actions';
import { isWidgetOpened } from '../../store/dispatcher';
import { AnyFunction } from '../../utils/types';
import { GlobalState, InteractiveButton } from '../../store/types';

import apiClient from '../../apiClient';
import WidgetLayout from './layout';
import { QuickReplyConfig } from 'src/utils/widgetSettings';

type Props = {
  title: string;
  titleAvatar?: string;
  subtitle: string;
  senderPlaceHolder: string;
  profileAvatar?: string;
  showCloseButton?: boolean;
  fullScreenMode?: boolean;
  hideHeader?: boolean;
  /** True only when the welcome greeting is in the thread (IMBEE-8955). */
  greetingShown?: boolean;
  autofocus?: boolean;
  customLauncher?: AnyFunction;
  handleNewUserMessage: AnyFunction;
  handleInteractiveButtonClicked?: AnyFunction;
  handleConversationScroll?: AnyFunction;
  handleTextInputChange?: (event: any) => void;
  chatId?: string;
  handleToggle?: AnyFunction;
  launcherOpenLabel?: string;
  launcherCloseLabel?: string;
  launcherOpenImg?: string;
  launcherCloseImg?: string;
  sendButtonAlt?: string;
  showTimeStamp?: boolean;
  widgetConfig?: any;
  /** IMBEE-8069 (T86) Areas A and C. */
  quickReplyConfig?: QuickReplyConfig;
  imagePreview?: boolean;
  zoomStep?: number;
  handleSubmit?: AnyFunction;
  emojis?: boolean;
};

function Widget({
  title,
  titleAvatar,
  subtitle,
  senderPlaceHolder,
  profileAvatar,
  showCloseButton,
  fullScreenMode,
  hideHeader,
  greetingShown,
  autofocus,
  customLauncher,
  handleNewUserMessage,
  handleInteractiveButtonClicked,
  handleConversationScroll,
  handleTextInputChange,
  chatId,
  handleToggle,
  launcherOpenLabel,
  launcherCloseLabel,
  launcherCloseImg,
  launcherOpenImg,
  sendButtonAlt,
  showTimeStamp,
  widgetConfig,
  quickReplyConfig,
  imagePreview,
  zoomStep,
  handleSubmit,
  emojis,
}: Props) {
  const dispatch = useDispatch();

  const showChat = useSelector((state: GlobalState) => state.behavior.showChat);
  const parentUrl = useSelector((state: GlobalState) => state.responses.parentUrl);
  const messageList = useSelector((state: GlobalState) => state.messages.messages);

  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const toggleConversation = () => {
    if (!showChat) {
      if (window.parent && parentUrl) {
        window.parent.postMessage('isShowingChat', parentUrl);
      }
      // console.log("isShowingChat")
    } else {
      if (window.parent && parentUrl) {
        window.parent.postMessage('isHidingChat', parentUrl);
      }
      // console.log("isHidingChat")
    }

    dispatch(toggleChat());
    handleToggle ? handleToggle(isWidgetOpened()) : null;
  };

  const handleMessageSubmit = (userInput, userComponent: ElementType, plainFiles) => {
    // userComponentRef.current.addEventListener('click', ()=>console.log("button clicked"));
    if (!userInput.trim()) {
      return;
    }

    handleSubmit?.(userInput);

    handleNewUserMessage(userInput, plainFiles, messageList.length);

    if (!plainFiles) dispatch(addUserMessage(userInput));
    else dispatch(addUserMessage(userInput, userComponent));
  };

  const handleCustomeComponentSubmit = (userInput) => {
    dispatch(renderCustomComponent(userInput));
  };

  const onInteractiveButtonClicked = (event, button: InteractiveButton, messageID) => {
    event.preventDefault();
    handleInteractiveButtonClicked?.(button, messageID);
  };

  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (!showChat) {
        if (window.parent && parentUrl) {
          window.parent.postMessage('isShowingChat', parentUrl);
        }
      }

      if (!isWidgetOpened()) {
        dispatch(toggleChat());
        handleToggle ? handleToggle(isWidgetOpened()) : null;
      }

      const { type, data } = event.data;
      if (type === 'EXTERNAL_MESSAGE') {
        if (!apiClient.sessionId) {
          setPendingMessage(data);
        } else {
          handleNewUserMessage(data);
          dispatch(addUserMessage(data));
        }
      }
    };

    // IMBEE-8954 (REQ-CWC-02) — the campaign message types are NOT handled here. Their listener
    // is mounted by `App` on its first commit (`campaign/pageMessageChannel.ts`), because this
    // component renders only once `isValidWidget` is true — three awaited calls after `GET
    // /widget` — and the loader's queue is flushed on the readiness signal that listener posts.
    // `EXTERNAL_MESSAGE` below is unchanged.
    const messageListener = (event: MessageEvent) => {
      if (event.data.type === 'EXTERNAL_MESSAGE') {
        handlePostMessage(event);
      }
    };

    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [dispatch, showChat, parentUrl]);

  useEffect(() => {
    const sendPendingMessage = async () => {
      if (pendingMessage) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        handleNewUserMessage(pendingMessage);
        dispatch(addUserMessage(pendingMessage));
        setPendingMessage(null);
      }
    };

    if (apiClient.sessionId) {
      sendPendingMessage();
    }
  }, [apiClient.sessionId, pendingMessage, dispatch]);

  return (
    <WidgetLayout
      onToggleConversation={toggleConversation}
      onSendMessage={handleMessageSubmit}
      onSendCustomComponent={handleCustomeComponentSubmit}
      onInteractiveButtonClicked={onInteractiveButtonClicked}
      onHandleConversationScroll={handleConversationScroll}
      title={title}
      titleAvatar={titleAvatar}
      subtitle={subtitle}
      senderPlaceHolder={senderPlaceHolder}
      profileAvatar={profileAvatar}
      showCloseButton={showCloseButton!}
      fullScreenMode={fullScreenMode!}
      hideHeader={hideHeader}
      greetingShown={greetingShown}
      autofocus={autofocus!}
      customLauncher={customLauncher}
      onTextInputChange={handleTextInputChange}
      chatId={chatId!}
      launcherOpenLabel={launcherOpenLabel!}
      launcherCloseLabel={launcherCloseLabel!}
      launcherCloseImg={launcherCloseImg!}
      launcherOpenImg={launcherOpenImg!}
      sendButtonAlt={sendButtonAlt!}
      showTimeStamp={showTimeStamp!}
      widgetConfig={widgetConfig}
      quickReplyConfig={quickReplyConfig}
      imagePreview={imagePreview}
      zoomStep={zoomStep}
      emojis={emojis}
    />
  );
}

export default Widget;
